import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { debounceTime, filter, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { merge, Observable, shareReplay, Subject, withLatestFrom } from 'rxjs';
import { BazisFormService } from '@bazis/form/services/form.service';
import {
    EntityFormArray,
    FormSavingStatus,
    FormSettings,
    ParentEntity,
} from '@bazis/form/models/form.types';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';

@Component({
    selector: 'bazis-draft-inner-default',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [BazisFormService],
    standalone: false
})
export class BazisDraftInnerDefaultComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    parentTransit$: Observable<any>;

    @Input()
    forceSave$: Observable<any>;

    @Input()
    id: string = '';

    @Input()
    desiredId: string = '';

    @Input() index?: number = null;

    @Input()
    parent: ParentEntity;

    @Input()
    parentEnabled: boolean = true;

    @Input()
    parentStatus: string; // for the cases when form validationSchema depends on parent's status

    @Input()
    parentStatusToSaveByButton: string; // parentStatus when child object should be saved by button (using @Input forceSave$)

    @Output()
    created: EventEmitter<string> = new EventEmitter();

    @Output()
    deleted: EventEmitter<string> = new EventEmitter();

    @Output()
    statusChanged: EventEmitter<string> = new EventEmitter();

    @Output()
    savingStatus: EventEmitter<FormSavingStatus> = new EventEmitter();

    @Output()
    haveChangesStatus: EventEmitter<boolean> = new EventEmitter();

    @Input()
    forceLoadEntities = true;

    validationSchemaByParentStatus = null; // define validationSchema by parent's status, that is received in this.parentStatus @Input

    emittedId = null;

    id$ = this.formService.id$.pipe(
        tap((id) => {
            this.emittedId = id;
            this.created.emit(id);
        }),
    );

    hasChanges$ = this.formService.haveUnsavedChanges$.pipe(
        tap((v) => {
            this.haveChangesStatus.emit(v);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    status$: Observable<any> = merge(
        this.id$,
        this.formService.status$,
        this.formService.form$,
    ).pipe(
        debounceTime(0),
        tap(() => {
            this.statusChanged.emit(this.formService.form.status);
        }),
    );

    savingStatus$ = this.formService.savingStatus$.pipe(
        tap((savingStatus: FormSavingStatus) => {
            this.savingStatus.emit(savingStatus);
        }),
    );

    formSettings: FormSettings = {};

    parentTransitDone$: Observable<any>;

    // isLoading
    isLoading = new TemplateObservable(true);

    changes$;

    saveByButton$;

    constructor(public formService: BazisFormService) {}

    ngOnInit() {
        this.initialize();
    }

    initialize() {
        this.formSettings.id = this.id || null;
        this.formSettings.parent = this.parent || null;
        this.formSettings.desiredId = this.desiredId || null;
        Object.keys(this.formSettings.fieldConfig).forEach((field) => {
            if (this[field]) {
                this.formSettings.fieldConfig[field].initialValue = this[field];
            }
        });

        if (this.validationSchemaByParentStatus && this.parentStatus) {
            this.formSettings.validationSchema = {
                ...this.formSettings.validationSchema,
                ...this.validationSchemaByParentStatus,
                default:
                    this.validationSchemaByParentStatus[this.parentStatus] ||
                    this.validationSchemaByParentStatus.default,
            };
        }
        this.setFormSettings();
        if (!this.parentTransit$) this.parentTransit$ = new Subject();
        this.parentTransitDone$ = this.parentTransit$.pipe(
            tap((transit) => {
                if (
                    !this.formSettings.clearFormAfterTransitions ||
                    this.formSettings.clearFormAfterTransitions.indexOf(transit) === -1
                ) {
                    this.formService.updateForm(!this.formSettings.canUseParentSchema);
                }
            }),
        );

        if (!this.forceSave$) this.forceSave$ = new Subject();

        this.changes$ = this.formService.haveUnsavedChanges$.pipe(
            withLatestFrom(this.formService.entity$),
            filter(
                ([changes, entity]) =>
                    !!changes &&
                    (!this.parentStatusToSaveByButton ||
                        (entity && this.parentStatus !== this.parentStatusToSaveByButton)),
            ),
            switchMap(() => this.formService.save()),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );

        this.saveByButton$ = this.forceSave$.pipe(
            tap(() => {
                this.formService.form.updateValueAndValidity();
            }),
            mergeMap(() => this.formService.haveUnsavedChanges$.pipe(take(1))),
            filter((needSave) => !!needSave),
            switchMap(() => this.formService.save()),
            shareReplay(SHARE_REPLAY_SETTINGS),
        );
    }

    setFormSettings() {
        this.formService.setFormSettings(this.formSettings, this.forceLoadEntities);
    }

    deleteEntity(id) {
        this.formService
            .delete(this.formSettings.entityType, id)
            .pipe(
                tap(() => {
                    this.deleted.emit();
                    this.haveChangesStatus.emit(false);
                }),
                take(1),
            )
            .subscribe();
    }

    // do not use. Remain to support usage in older apps
    deleteOperation(id) {
        this.deleteEntity(id);
    }

    getArray(form, fieldName) {
        return form.get(fieldName) as EntityFormArray;
    }

    ngOnDestroy() {}

    ngOnChanges(changes: SimpleChanges): void {
        if (
            this.validationSchemaByParentStatus &&
            changes.parentStatus &&
            changes.parentStatus.currentValue &&
            changes.parentStatus.previousValue &&
            changes.parentStatus.previousValue !== changes.parentStatus.currentValue
        ) {
            this.formService.updateForm(!this.formSettings.canUseParentSchema, {
                validationSchema: {
                    ...this.validationSchemaByParentStatus,
                    default:
                        this.validationSchemaByParentStatus[this.parentStatus] ||
                        this.validationSchemaByParentStatus.default,
                },
            });
        }
    }
}
